/* AppData.store.js */
import firebase from 'firebase'
import db from '@/firebase/init'
import axios from 'axios'
import { functionUrl } from '@/helpers/firebaseEnv'
import {
  collectRefIds,
  parsePreviewArgs,
  transformUnitForLanguage,
  transformWeekForLanguage,
} from '@/helpers/previewContent'

// Storage retries a failed download for up to 2 minutes. A course that has
// not loaded by then is reported as failed, so the menu offers a retry (#115).
const COURSE_LOAD_TIMEOUT_MS = 20000

function withTimeout(promise, ms) {
  let timer
  const timeout = new Promise((resolve, reject) => {
    timer = setTimeout(() => reject(new Error('Timed out loading the course')), ms)
  })
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer))
}

const initialState = () => ({
  cid: null,
  churches: [],
  units: [],
  status: null,
  error: null,
  isRunning: false,
  internalActiveNotifications: [],
  allInternalNotifications: [],
  notifications: [],
  notificationFeedback: null,
  course: [],
  sections: [],
  missions: [],
  weeks: [],
  features: [],
  supportChatConfig: {
    provider: 'lhc',
    lhc: {},
    zendesk: {},
    lifeOnLifeSupport: {},
  },
  // steps: [],
  attemptedTries: 0,
  courseDrawer: false,
  notesOpen: false,
  highlightsOpen: false,
  commentsOpen: false,
  archivedUserCourses: [],
  userActiveCourse: [],
  activeUnit: null,
  prevUnit: null,
  prevUnitId: null,
  prevWeek: null,
  prevSection: null,
  prevSectionId: null,
  prevMission: null,
  // completedUnit: null,
  // courseChanged: null
})

// State object
const state = initialState()

// Getter functions
const getters = {
  cid(state) {
    return state.cid
  },
  churches(state) {
    return state.churches
  },
  getChurchById(state) {
    // console.log(state)
    return (cid) =>
      state.churches.filter((item) => {
        return item.cid === cid
      })
  },
  isRunning(state) {
    return state.isRunning
  },
  courseDrawer(state) {
    return state.courseDrawer
  },
  notesOpen(state) {
    return state.notesOpen
  },
  highlightsOpen(state) {
    return state.highlightsOpen
  },
  commentsOpen(state) {
    return state.commentsOpen
  },
  getActiveInternalNotifications(state) {
    return state.internalActiveNotifications
  },
  getAllInternalNotifications(state) {
    return state.allInternalNotifications
  },
  notifications(state) {
    return state.notifications
  },
  getNotificationFeedback(state) {
    return state.notificationFeedback
  },
  units(state) {
    return state.units
  },
  getUnitByOrder(state) {
    return (unit) =>
      state.units.filter((item) => {
        return item.unit === unit.order && item.group === unit.group
      })
  },
  activeUnit(state) {
    return state.activeUnit
  },
  prevUnit(state) {
    return state.prevUnit
  },
  prevWeek(state) {
    return state.prevWeek
  },
  prevSection(state) {
    return state.prevSection
  },
  prevMission(state) {
    return state.prevMission
  },
  getCourse(state) {
    return state.course
  },
  getActiveCourse(state) {
    return state.userActiveCourse
  },
  getArchivedCourses(state) {
    return state.archivedUserCourses
  },
  getFeatures(state) {
    return state.features
  },
  getSupportChatConfig(state) {
    return (
      state.supportChatConfig || {
        provider: 'lhc',
        lhc: {},
        zendesk: {},
        lifeOnLifeSupport: {},
      }
    )
  },
  getSections(state) {
    return state.sections
  },
  getUnits(state) {
    return state.units
  },
  getMissions(state) {
    return state.missions
  },
  getWeeks(state) {
    return state.weeks
  },
  getAttemptedTries(state) {
    return state.attemptedTries
  },
  status(state) {
    return state.status
  },
  error(state) {
    return state.error
  },
}

// Actions
const actions = {
  reset({ commit }) {
    commit('RESET')
  },
  setChurchId(context, payload) {
    context.commit('setChurchId', payload)
  },
  addChurch(context, payload) {
    context.commit('addChurch', payload)
  },
  allChurches(context, payload) {
    context.commit('allChurches', payload)
  },
  updateChurch(context, payload) {
    context.commit('updateChurch', payload)
  },
  updateChurchLocale(context, payload) {
    context.commit('updateChurchLocale', payload)
  },
  isRunning(context, payload) {
    context.commit('isRunning', payload)
  },
  courseDrawer(context, payload) {
    context.commit('courseDrawer', payload)
  },
  notesOpen(context, payload) {
    context.commit('notesOpen', payload)
  },
  highlightsOpen(context, payload) {
    context.commit('highlightsOpen', payload)
  },
  commentsOpen(context, payload) {
    context.commit('commentsOpen', payload)
  },
  addNotification(context, payload) {
    context.commit('addNotification', payload)
  },
  resetNotifications(context, payload) {
    context.commit('allNotifications', payload)
  },
  addCourse(context, payload) {
    context.commit('addCourse', payload)
  },
  addUserActiveCourse(context, payload) {
    context.commit('addUserActiveCourse', payload)
  },
  addArchivedUserCourses(context, payload) {
    context.commit('addArchivedUserCourses', payload)
  },
  async getCourse({ dispatch }, { cid, userid }) {
    let g
    let coursearray = []

    // initialize the query
    let groupRef = db.collection('groups')

    // only get group for specific church
    groupRef = groupRef.where('cid', '==', cid)

    // check if the user is a member or leader
    const isMember = groupRef
      .where('members', 'array-contains', userid.uid)
      .get()
    const isLeader = groupRef.where('leader', '==', userid.uid).get()

    // wait for the results
    const [isMemberQuerySnapshot, isLeaderQuerySnapshot] =
      await Promise.allSettled([isMember, isLeader])

    // get the docs from the results
    const isMemberArray = isMemberQuerySnapshot.value.docs
    const isLeaderArray = isLeaderQuerySnapshot.value.docs

    // merge docs arrays into one
    const userArray = isMemberArray.concat(isLeaderArray)

    // get the user data
    userArray.forEach((doc) => {
      g = doc.data()
      g.id = doc.id
    })

    // if we have user data find the courses
    if (g) {
      return new Promise((res, rej) => {
        let courseRef = db.collection('courses')
        courseRef = courseRef.where('cid', '==', cid)
        courseRef = courseRef.where('selectedGroup', 'array-contains', {
          id: g.id,
        })
        courseRef = courseRef.where('archive', '==', false)
        courseRef
          .get()
          .then((snapshot) => {
            snapshot.forEach((doc) => {
              let c = doc.data()
              c.id = doc.id

              coursearray.push({
                archive: c.archive,
                id: c.id,
                title: c.title,
                baseYear: c.baseYear,
                cid: c.cid,
                startDate: c.startDate,
                status: c.status,
                sections: c.sections,
                units: c.units,
                selectedGroup: c.selectedGroup,
                mission: c.mission,
              })
            })
          })
          .then(() => {
            // console.log('.5 ', coursearray)
            dispatch('addCourse', coursearray)
            // dispatch('getSections')
            // dispatch('getUnits')
            res()
          })
          .catch(() => rej())
      })
    }
  },
  addActiveInternalNotification(context, payload) {
    context.commit('addActiveInternalNotification', payload)
  },
  resetActiveInternaleNotifications(context, payload) {
    context.commit('allActiveInternalNotifications', payload)
  },
  async getActiveInternalNotifications({ dispatch }, { cid, user }) {
    if (cid && user) {
      let internalActiveNotifications = []

      // Get all the notifications for this church
      let getAllNotifications = await db
        .collection('notifications')
        .where('cid', '==', cid)
        .where('active', '==', true)
        .orderBy('created')
        .get()

      for (const notificationDoc of getAllNotifications.docs) {
        let notification = notificationDoc.data()
        notification.id = notificationDoc.id

        // check to make sure this notification has not already been read
        let getAllReadNotifications = await db
          .collection('notificationsread')
          .where('notificationid', '==', notification.id)
          .where('userid', '==', user.uid)
          .get()

        if (getAllReadNotifications.empty) {
          internalActiveNotifications.push(notification)
        }
      }

      dispatch('addActiveInternalNotification', internalActiveNotifications)
    }
  },
  addInternalNotification(context, payload) {
    context.commit('addInternalNotification', payload)
  },
  resetAllInternaleNotifications(context, payload) {
    context.commit('allInternalNotifications', payload)
  },
  async getAllInternalNotifications({ dispatch }, { cid, user }) {
    // console.log('getAllInternalNotifications')
    if (cid && user) {
      let allInternalActiveNotifications = []

      // Reset existing notifications before fetching new ones
      dispatch('resetAllInternaleNotifications', [])

      // Get all the notifications for this church
      let getAllNotifications = await db
        .collection('notifications')
        .where('cid', '==', cid)
        .where('active', '==', true)
        .orderBy('created')
        .get()

      for (const notificationDoc of getAllNotifications.docs) {
        let notification = notificationDoc.data()
        notification.id = notificationDoc.id


        // check to make sure this notification has not already been read
        let getAllReadNotifications = await db
          .collection('notificationsread')
          .where('notificationid', '==', notification.id)
          .where('userid', '==', user.uid)
          .get()

        if (!getAllReadNotifications.empty) {
          notification.hasRead = true
        }

        const exists = allInternalActiveNotifications.some(
          (n) => n.id === notification.id
        )
        if (!exists) {
          allInternalActiveNotifications.push(notification)
        }
      }

      // console.log('internalActiveNotifications ', allInternalActiveNotifications)

      dispatch('resetAllInternaleNotifications', allInternalActiveNotifications)
    }
  },
  // Rejects when the user has an active enrollment that could not be loaded,
  // so the menu offers a retry instead of "Start Your Journey", which would
  // create a second enrollment (GitHub #115).
  async getActiveCourse({ dispatch }, { cid, user }) {
    // console.log(cid, user)
    let userActiveCourse = []
    let archivedUserCourses = []
    let loadFailed = false

    try {
      let currentCourseRef = db
        .collection('currentcourses')
        .where('cid', '==', cid)
        .where('user', '==', user.uid)
        .where('status', '==', 'active')

      let activeRef = await currentCourseRef.get({ source: 'server' })

      if (!activeRef.empty) {
        for (const doc of activeRef.docs) {
          let course = doc.data()
          course.id = doc.id

          // Debugging log for course data
          // console.log(`Checking course ${course.id}`, course);

          // Validate course.storageRef
          if (
            !course.storageRef ||
            typeof course.storageRef !== 'string' ||
            course.storageRef.trim() === ''
          ) {
            console.warn(
              `⚠️ Skipping course ${course.id}: Invalid storageRef`,
              course.storageRef
            )
            continue // Skip this course
          }

          // Validate course.missions
          if (
            !course.missions ||
            typeof course.missions !== 'string' ||
            course.missions.trim() === ''
          ) {
            console.warn(
              `⚠️ Skipping course ${course.id}: Invalid missions`,
              course.missions
            )
            continue // Skip this course
          }

          try {
            const storage = firebase.storage()
            const storageRef = storage.ref()

            // console.log(`Fetching storage data for ${course.id}: storageRef="${course.storageRef}", missions="${course.missions}"`);

            const { resp, missionResp } = await withTimeout(
              (async () => {
                const objectRef = storageRef.child(course.storageRef)
                const url = await objectRef.getDownloadURL()
                const resp = await axios.get(url)

                const missionRef = storageRef.child(course.missions)
                const missionURL = await missionRef.getDownloadURL()
                const missionResp = await axios.get(missionURL)
                return { resp, missionResp }
              })(),
              COURSE_LOAD_TIMEOUT_MS
            )

            resp.data.storageID = course.storageID || 'Unknown'
            resp.data.missions = missionResp.data || {}

            if (course.status === 'active') {
              userActiveCourse.push(resp.data)
            } else if (resp.data.id && resp.data.title) {
              // Ensure only valid courses are added to archived list
              archivedUserCourses.push({
                id: resp.data.id,
                baseYear: resp.data.baseYear || 'Unknown',
                title: resp.data.title,
                storageID: course.storageID || 'Unknown',
                cid: resp.data.cid || 'Unknown',
                missions: course.missions || 'Unknown',
              })
            } else {
              console.warn(
                `⚠️ Skipping archived course ${course.id}: Missing required fields.`
              )
            }
          } catch (fetchError) {
            loadFailed = true
            console.error(
              `❌ Error fetching data for course ${course.id}:`,
              fetchError
            )
          }
        }
      }

      if (loadFailed && userActiveCourse.length === 0) {
        throw new Error('Your active course could not be loaded.')
      }

      dispatch('addUserActiveCourse', userActiveCourse)

      /* *
       * we have turned this off since we are now getting
       * the archived courses from the Current Course Menu component
       * */
      // dispatch('addArchivedUserCourses', archivedUserCourses)

      return userActiveCourse
    } catch (error) {
      console.error('Error retrieving active courses:', error)
      throw error
    }
  },
  resetCourse(context, payload) {
    context.commit('allCourses', payload)
  },
  getActiveFeatures({ commit, dispatch }) {
    // get all active features
    let activeFeatures = []
    let activeFeaturesRef = db.collection('newfeatures')
    activeFeaturesRef = activeFeaturesRef.where('active', '==', true)
    activeFeaturesRef
      .get()
      .then((snapshot) => {
        snapshot.forEach((doc) => {
          let f = doc.data()
          f.id = doc.id
          activeFeatures.push(f.slug)
        })
      })
      .then(() => {
        commit('setFeatures', activeFeatures)
        dispatch('getSupportChatConfig')
      })
  },
  async getSupportChatConfig({ commit }) {
    try {
      const doc = await db.collection('config').doc('supportChat').get()
      const data = doc.exists ? doc.data() || {} : {}
      const provider = ['none', 'lhc', 'zendesk', 'lifeOnLifeSupport'].includes(
        data.provider,
      )
        ? data.provider
        : 'lhc'
      commit('setSupportChatConfig', {
        provider,
        lhc: data.lhc && typeof data.lhc === 'object' ? data.lhc : {},
        zendesk:
          data.zendesk && typeof data.zendesk === 'object' ? data.zendesk : {},
        lifeOnLifeSupport:
          data.lifeOnLifeSupport &&
          typeof data.lifeOnLifeSupport === 'object'
            ? data.lifeOnLifeSupport
            : {},
      })
    } catch (error) {
      console.warn('Error loading support chat config:', error)
      commit('setSupportChatConfig', {
        provider: 'lhc',
        lhc: {},
        zendesk: {},
        lifeOnLifeSupport: {},
      })
    }
  },
  addSections(context, payload) {
    context.commit('addSections', payload)
  },
  getSections({ dispatch, rootGetters }) {
    // get all sections
    let secs = []
    const roles = rootGetters['UserData/getRoles'] || []
    const userCid = rootGetters['AppData/cid']
    const isSuper =
      Array.isArray(roles) &&
      (roles.includes('superAdmin') || roles.includes('helpDesk'))
    let sectionRef = db.collection('sections')
    sectionRef
      .get()
      .then((snapshot) => {
        snapshot.forEach((doc) => {
          let s = doc.data()
          s.id = doc.id
          const canView = isSuper
            ? true
            : (s.type === 'public' && s.status === 'verified') ||
              (s.cid && userCid && s.cid === userCid)
          if (canView) secs.push(s)
        })
      })
      .then(() => {
        dispatch('addSections', secs)
      })
  },
  addUnits(context, payload) {
    context.commit('addUnits', payload)
  },
  getUnits({ dispatch }) {
    // get all units
    let units = []
    let unitRef = db.collection('units')
    unitRef
      .get()
      .then((snapshot) => {
        snapshot.forEach((doc) => {
          let u = doc.data()
          u.id = doc.id
          units.push(u)
        })
      })
      .then(() => {
        dispatch('addUnits', units)
      })
  },
  setActiveUnit(context, payload) {
    context.commit('setActiveUnit', payload)
  },
  setPrevUnit({ commit }, unit) {
    commit('setPrevUnit', unit)
    commit('setPrevUnitId', null) // clear persisted id when using a live preview
  },
  setPrevUnitId({ commit }, id) {
    commit('setPrevUnit', null) // clear current unit so we can fetch it
    commit('setPrevUnitId', id)
  },
  async fetchPrevUnitById({ commit }, idOrOpts) {
    const { id, lang } = parsePreviewArgs(idOrOpts)
    if (!id) {
      commit('setPrevUnit', null)
      return
    }
    const snap = await db.collection('units').doc(id).get()
    if (snap.exists) {
      commit(
        'setPrevUnit',
        transformUnitForLanguage({ id: snap.id, ...snap.data() }, lang)
      )
    } else {
      commit('setPrevUnit', null)
    }
  },
  setPrevWeek(context, payload) {
    context.commit('setPrevWeek', payload)
  },
  async fetchPrevWeekById({ commit }, idOrOpts) {
    const { id, lang } = parsePreviewArgs(idOrOpts)
    if (!id) {
      commit('setPrevWeek', null)
      return
    }
    const snap = await db.collection('weeks').doc(id).get()
    if (snap.exists) {
      commit(
        'setPrevWeek',
        transformWeekForLanguage({ id: snap.id, ...snap.data() }, lang)
      )
    } else {
      commit('setPrevWeek', null)
    }
  },
  setPrevSection({ commit }, section) {
    commit('setPrevSection', section)
    commit('setPrevSectionId', null) // clear persisted id when using a live preview
  },
  setPrevSectionId({ commit }, id) {
    commit('setPrevSection', null) // clear current section so we can fetch it
    commit('setPrevSectionId', id)
  },
  async fetchPrevSectionById({ commit }, idOrOpts) {
    const { id, lang } = parsePreviewArgs(idOrOpts)
    if (!id) {
      commit('setPrevSection', null)
      return
    }
    const snap = await db.collection('sections').doc(id).get()
    if (!snap.exists) {
      commit('setPrevSection', null)
      return
    }
    const section = { id: snap.id, ...snap.data() }
    const previewLang = lang || section.requiredLanguage || 'en'
    const unitIds = collectRefIds(section.units)
    const selectedUnits = (
      await Promise.all(
        unitIds.map(async (unitId) => {
          const unitSnap = await db.collection('units').doc(unitId).get()
          if (!unitSnap.exists) return null
          return transformUnitForLanguage(
            { id: unitSnap.id, ...unitSnap.data() },
            previewLang
          )
        })
      )
    ).filter(Boolean)

    commit('setPrevSection', {
      id: section.id,
      title: section.title || section.sectionTitle,
      requiredLanguage: previewLang,
      selectedUnits,
    })
  },

  setPrevMission(context, payload) {
    context.commit('setPrevMission', payload)
  },
  async fetchPrevMissionById({ commit }, idOrOpts) {
    const { id, lang } = parsePreviewArgs(idOrOpts)
    if (!id) {
      commit('setPrevMission', null)
      return
    }
    const snap = await db.collection('missions').doc(id).get()
    if (!snap.exists) {
      commit('setPrevMission', null)
      return
    }
    const mission = { id: snap.id, ...snap.data() }
    const previewLang = lang || mission.requiredLanguage || 'en'
    const weekIds = collectRefIds(mission.weeks)
    const selectedWeeks = (
      await Promise.all(
        weekIds.map(async (weekId) => {
          const weekSnap = await db.collection('weeks').doc(weekId).get()
          if (!weekSnap.exists) return null
          return transformWeekForLanguage(
            { id: weekSnap.id, ...weekSnap.data() },
            previewLang
          )
        })
      )
    ).filter(Boolean)

    commit('setPrevMission', {
      id: mission.id,
      title: mission.title,
      requiredLanguage: previewLang,
      selectedWeeks,
    })
  },
  resetUnits(context, payload) {
    context.commit('allUnits', payload)
  },
  addMissions(context, payload) {
    context.commit('addMissions', payload)
  },
  getMissions({ dispatch }) {
    // get all missions
    let missions = []
    let missionRef = db.collection('missions')
    missionRef
      .get()
      .then((snapshot) => {
        snapshot.forEach((doc) => {
          let m = doc.data()
          m.id = doc.id
          missions.push(m)
        })
      })
      .then(() => {
        dispatch('addMissions', missions)
      })
  },
  addWeeks(context, payload) {
    context.commit('addWeeks', payload)
  },
  getWeeks({ dispatch }) {
    // get all weeks
    let weeks = []
    let weekRef = db.collection('weeks')
    weekRef
      .get()
      .then((snapshot) => {
        snapshot.forEach((doc) => {
          let w = doc.data()
          w.id = doc.id
          weeks.push(w)
        })
      })
      .then(() => {
        dispatch('addWeeks', weeks)
      })
  },
  notification({ commit, dispatch }, { leader, meta, type, cid }) {
    commit('setNotificationFeedback', null)
    // console.log('sendGrid ', leader, meta, type, cid)
    if (type == 'Unit edited') {
      // GET ALL THE ADMINS FOR THIS CHURCH AND SEND AN EMAIL TO EACH
      let ref = db
        .collection('users')
        .where('cid', '==', cid)
        .where('roles', 'array-contains', 'superAdmin')
      ref.get().then((snapshot) => {
        snapshot.forEach((doc) => {
          let s = doc.data()
          s.id = doc.id

          // console.log('Unit edited, superAdmins are ', s.email)

          // FOR TESTING I AM SENDING TO MY EMAILS ONLY
          // REMOVE THE IF STATEMENT WHEN WE GO LIVE
          // console.log('we are still running in dev mode only using your two email account')
          // if(s.email == 'jason@rayrok.com') {
          dispatch('sendGrid', {
            leader: leader,
            meta: meta,
            type: type,
            email: s.email,
          })
          // }
        })
      })
    }
    if (type == 'Section edited') {
      // GET ALL THE ADMINS FOR THIS CHURCH AND SEND AN EMAIL TO EACH
      let ref = db
        .collection('users')
        .where('cid', '==', cid)
        .where('roles', 'array-contains', 'superAdmin')
      ref.get().then((snapshot) => {
        snapshot.forEach((doc) => {
          let s = doc.data()
          s.id = doc.id

          // console.log('Section edited, superAdmins are ', s.email)

          // FOR TESTING I AM SENDING TO MY EMAILS ONLY
          // REMOVE THE IF STATEMENT WHEN WE GO LIVE
          // console.log('we are still running in dev mode only using your two email account')
          // if(s.email == 'jason@rayrok.com') {
          dispatch('sendGrid', {
            leader: leader,
            meta: meta,
            type: type,
            email: s.email,
          })
          // }
        })
      })
    }
    if (type == 'Section approved') {
      // GET THE LEADER AND SEND AN EMAIL
      let ref = db
        .collection('users')
        .where('cid', '==', cid || meta?.cid || null)
        .where('uid', '==', leader)
      ref.get().then((snapshot) => {
        snapshot.forEach((doc) => {
          let s = doc.data()
          s.id = doc.id

          // console.log('Section approved, admin is ', s.email)

          dispatch('sendGrid', {
            leader: s,
            meta: meta,
            type: type,
            email: s.email,
          })
        })
      })
    }
    if (type == 'Course created') {
      // GET ALL THE ADMINS FOR THIS CHURCH AND SEND AN EMAIL TO EACH
      let ref = db
        .collection('users')
        .where('cid', '==', cid)
        .where('roles', 'array-contains', 'admin')
      ref.get().then((snapshot) => {
        snapshot.forEach((doc) => {
          let s = doc.data()
          s.id = doc.id

          // console.log('admin is ', s.email)

          // send an email to each admin of this church
          // (NOTE: there should only be 1 but the super admin could change that)

          // FOR TESTING I AM SENDING TO MY EMAILS ONLY
          // REMOVE THE IF STATEMENT WHEN WE GO LIVE
          // console.log('we are still running in dev mode only using your two email account')
          // if(s.email == 'jsd219@gmail.com' || s.email == 'jsd219@me.com') {
          dispatch('sendGrid', {
            leader: leader,
            meta: meta,
            type: type,
            email: s.email,
          })
          // }
        })
      })
    }
    if (type == 'Course approved') {
      // GET THE LEADER AND SEND AN EMAIL
      let ref = db
        .collection('users')
        .where('cid', '==', cid || meta?.cid || null)
        .where('uid', '==', leader)
      ref.get().then((snapshot) => {
        snapshot.forEach((doc) => {
          let s = doc.data()
          s.id = doc.id

          // console.log('leader is ', s.email)

          dispatch('sendGrid', {
            leader: s,
            meta: meta,
            type: type,
            email: s.email,
          })
        })
      })
    }
  },
  async logError({ commit }, errorDetails) {
    try {
      // Ensure timestamp is a Firestore Timestamp object
      if (
        !errorDetails.timestamp ||
        typeof errorDetails.timestamp === 'number'
      ) {
        errorDetails.timestamp = firebase.firestore.Timestamp.now()
      }

      // Ensure stack trace is a valid string, replace undefined with an empty string
      if (!errorDetails.stack || typeof errorDetails.stack !== 'string') {
        errorDetails.stack = 'No stack trace available'
      }

      await db.collection('errorLogs').add(errorDetails)
      console.log('Error logged to Firestore successfully')
    } catch (err) {
      console.error('Failed to log error to Firestore:', err)
      commit('setError', err)
    }
  },
  sendGrid({ commit }, { leader, meta, type, email }) {
    axios({
      method: 'post',
      url: functionUrl('sendgrid'),
      data: {
        email: email,
        meta: meta,
        leader: leader,
        type: type,
      },
    })
      .then(() => {
        // console.log('res ', response);
        let msg
        if (type == 'Course created')
          msg = 'A notification has been sent to the church admin'
        if (type == 'Course approved')
          msg = 'A notification has been sent to the group leader'
        if (type == 'Section created')
          msg = 'A notification has been sent to the superadmin'
        if (type == 'Section approved')
          msg = 'A notification has been sent to the church admin'

        commit('setNotificationFeedback', {
          code: 'notification-sent',
          message: msg,
        })
      })
      .catch((error) => {
        console.log('err ', error)
        commit('setNotificationFeedback', {
          code: 'notification-error',
          message: 'There was an error sending your notification ' + error,
        })
      })
  },
  /**
   * Sets AppData.cid + church list so church admins are scoped to their church on first load
   * (e.g. after curriculum-site API signup + redirect) without requiring logout/login.
   */
  async hydrateChurchContextFromCid({ dispatch }, cid) {
    if (!cid || typeof cid !== 'string') return

    dispatch('resetNotifications', [])
    dispatch('resetUnits', [])
    dispatch('isRunning', false)

    dispatch('setChurchId', cid)

    const snap = await db.collection('churches').where('cid', '==', cid).get()
    const churches = []
    snap.forEach((doc) => {
      churches.push({
        id: doc.id,
        churchtitle: doc.data().churchtitle,
        cid: doc.data().cid,
        url: doc.data().url,
        churchLocale: doc.data().churchLocale || null,
      })
    })
    if (churches.length > 0) {
      dispatch('allChurches', churches)
    }
  },
  // updateAttemptedTries(context, payload) {
  //   context.commit('updateAttemptedTries', payload)
  // },
  // isAuthorized(context, payload) {
  //     const found = payload.arr1.some(r => payload.arr2.includes(r))
  //     return found
  // },
}

// Mutations
const mutations = {
  RESET(state) {
    const newState = initialState()
    // console.log('reset called 3', newState)
    Object.keys(newState).forEach((key) => {
      state[key] = newState[key]
    })
  },
  setChurchId(state, payload) {
    state.cid = payload
  },
  allChurches(state, payload) {
    state.churches = payload
  },
  addChurch(state, payload) {
    state.churches.push(payload)
  },
  updateChurch(state, payload) {
    let objIndex = state.churches.findIndex((obj) => obj.cid == payload.cid)
    state.churches[objIndex].churchtitle = payload.churchtitle
    state.churches[objIndex].active = payload.active
  },
  updateChurchLocale(state, payload) {
    const objIndex = state.churches.findIndex((obj) => obj.cid === payload.cid)
    if (objIndex === -1) {
      return
    }
    state.churches[objIndex].churchLocale = payload.churchLocale
  },
  setStatus(state, payload) {
    state.status = payload
  },
  isRunning(state, payload) {
    state.isRunning = payload
  },
  courseDrawer(state, payload) {
    state.courseDrawer = payload
  },
  notesOpen(state, payload) {
    state.notesOpen = payload
  },
  highlightsOpen(state, payload) {
    state.highlightsOpen = payload
  },
  commentsOpen(state, payload) {
    state.commentsOpen = payload
  },
  allNotifications(state, payload) {
    state.notifications = payload
  },
  addNotification(state, payload) {
    state.notifications.push(payload)
  },
  allActiveInternalNotifications(state, payload) {
    state.internalActiveNotifications = payload
  },
  addActiveInternalNotification(state, payload) {
    state.internalActiveNotifications.push(payload)
  },
  allInternalNotifications(state, payload) {
    state.allInternalNotifications = payload
  },
  addInternalNotification(state, payload) {
    const exists = state.allInternalNotifications.some(
      (n) => n.id === payload.id
    )
    if (!exists) {
      state.allInternalNotifications.push(payload)
    }
  },
  setNotificationFeedback(state, payload) {
    state.notificationFeedback = payload
  },
  allCourses(state, payload) {
    state.course = payload
  },
  addCourse(state, payload) {
    state.course = payload
  },
  addUserActiveCourse(state, payload) {
    state.userActiveCourse = payload
  },
  addArchivedUserCourses(state, payload) {
    state.archivedUserCourses = payload
  },
  allSections(state, payload) {
    state.sections = payload
  },
  addSections(state, payload) {
    state.sections.push(payload)
  },
  setFeatures(state, payload) {
    state.features = payload
  },
  setSupportChatConfig(state, payload) {
    state.supportChatConfig = payload
  },
  allUnits(state, payload) {
    state.units = payload
  },
  addUnits(state, payload) {
    state.units.push(payload)
  },
  setActiveUnit(state, payload) {
    state.activeUnit = payload
  },
  setPrevUnit(state, payload) {
    state.prevUnit = payload
  },
  setPrevUnitId(state, payload) {
    state.prevUnitId = payload
  },
  setPrevWeek(state, payload) {
    state.prevWeek = payload
  },
  setPrevSection(state, payload) {
    state.prevSection = payload
  },
  setPrevSectionId(state, payload) {
    state.prevSectionId = payload
  },

  setPrevMission(state, payload) {
    state.prevMission = payload
  },
  // setCompletedUnit(state, payload) {
  //     state.completedUnit = payload
  // },
  // setCourseChanged(state, payload) {
  //     state.courseChanged = payload
  // },
  allMissions(state, payload) {
    state.missions = payload
  },
  addMissions(state, payload) {
    state.missions.push(payload)
  },
  allWeeks(state, payload) {
    state.weeks = payload
  },
  addWeeks(state, payload) {
    state.weeks.push(payload)
  },
  // updateAttemptedTries(state, payload) {
  //   state.attemptedTries = payload;
  // },
  setError(state, payload) {
    state.error = payload
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
